import { inject } from '@angular/core';
import { InjectableSuperclass } from '@sama/ng-core';
import { merge, NEVER } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { KeyboardShortcutService } from '../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { scalePoint } from '../../../shared/misc-utilities/point-utils';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';

interface ShortcutNames {
  rightShortcutName: string;
  leftShortcutName: string;
  upShortcutName: string;
  downShortcutName: string;
}

/**
 * Abstract class that helps convert keyboard shortcuts to X,Y offset
 */
export abstract class AbstractKeyboardMovementService extends InjectableSuperclass {
  private keyboardShortcutService = inject(KeyboardShortcutService);

  protected createOffset$(
    {
      rightShortcutName,
      leftShortcutName,
      upShortcutName,
      downShortcutName,
    }: Partial<ShortcutNames>,
    getDelta: () => number,
  ) {
    return merge(
      this.getOffset$(leftShortcutName, getDelta, [-1, 0]),
      this.getOffset$(rightShortcutName, getDelta, [1, 0]),
      this.getOffset$(upShortcutName, getDelta, [0, -1]),
      this.getOffset$(downShortcutName, getDelta, [0, 1]),
    );
  }

  private getOffset$(
    shortcutName: string | undefined,
    getDelta: () => number,
    vector: Point2D,
  ) {
    return shortcutName
      ? this.keyboardShortcutService.getMatchingEvent$(shortcutName).pipe(
          tap(({ keyboardEvent }) => {
            keyboardEvent.preventDefault();
          }),
          map(() => scalePoint(vector, getDelta())),
        )
      : NEVER;
  }
}
